import { CurrencyPipe, NgClass, TitleCasePipe } from "@angular/common";
import { Component, computed, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { disabled, form, FormField, maxLength } from "@angular/forms/signals";
import { CampaignType, Marketplace, MediaType } from "@front/m19-api-client";
import { StrategyService } from "@front/m19-injection-token";
import { CurrencySymbols, Marketplaces, StrategyEx, StrategyUpdateParams } from "@front/m19-models";
import {
  AccountSelectionService,
  AmazonProductImageUrlPipe,
  AmazonProductUrlPipe,
  AuthService,
  AsinService,
  DspCreativeAssetsService,
} from "@front/m19-services";
import {
  ConfirmPopupComponent,
  ConfirmPopupData,
  IButtonDirective,
  IFormFieldComponent,
  IIconComponent,
  IInputGroupDirective,
  IInputGroupInput,
  IInputGroupSuffixDirective,
  ITooltipDirective,
  IToggleComponent,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { DspCreativeComponent } from "@m19-board/dsp/dsp-creatives/dsp-creative/dsp-creative.component";
import { UploadAmazonLoadingModalComponent } from "@m19-board/shared/media/upload-amazon-loading-modal/upload-amazon-loading-modal.component";
import { dspHeadlineLimit } from "@front/m19-models/DspLimits";
import { StrategyPageService } from "@m19-board/strategies/strategy-page/strategy-page.service";
import { ReviewScoreComponent } from "@m19-board/tracking/review-score/review-score.component";
import { filter, switchMap, take, tap } from "rxjs";

@Component({
  selector: "app-dsp-responsive-ecommerce",
  imports: [
    AmazonProductImageUrlPipe,
    AmazonProductUrlPipe,
    CurrencyPipe,
    DspCreativeComponent,
    IButtonDirective,
    NgClass,
    ReviewScoreComponent,
    TitleCasePipe,
    TranslocoDirective,
    IToggleComponent,
    ITooltipDirective,
    IFormFieldComponent,
    FormField,
    IInputGroupDirective,
    IInputGroupInput,
    IInputGroupSuffixDirective,
    IIconComponent,
  ],
  templateUrl: "./dsp-responsive-ecommerce.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspResponsiveEcommerceComponent {
  private readonly strategyPageService = inject(StrategyPageService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly strategyService = inject(StrategyService);
  private readonly toasterService = inject(ToasterService);
  private readonly asinService = inject(AsinService);
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  private readonly dspCreativeAssetsService = inject(DspCreativeAssetsService);
  private readonly authService = inject(AuthService);

  protected readonly MediaType = MediaType;
  private readonly accountMarketplace = this.strategyPageService.accountMarketplace;

  readonly strategy = this.strategyPageService.strategy;
  readonly headlineMaxLength = computed(() => dspHeadlineLimit(this.strategy()?.marketplace));

  isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, { requireSync: true });
  logoAssetId = computed(() => this.strategy()?.logoAssetId ?? "");
  firstProductInStrategy = toSignal(
    toObservable(this.strategy).pipe(
      filter((strategy): strategy is StrategyEx => strategy !== undefined),
      switchMap((s) => this.asinService.getProductWithMarketplace(s.asins[0].asin, s.marketplace)),
    ),
  );

  protected readonly locale = toSignal(this.authService.userLocale$, { requireSync: true });
  protected readonly productCurrency = computed(() => {
    const marketplace: Marketplace | undefined = this.strategy()?.marketplace;
    return marketplace ? Marketplaces[marketplace].currency : undefined;
  });
  protected readonly productCurrencySymbol = computed(() => {
    const currency = this.productCurrency();
    return currency ? CurrencySymbols[currency] : "";
  });

  readonly headlineForm = form(signal<string>(this.strategy()?.headline ?? ""), (headline) => {
    maxLength(headline, () => this.headlineMaxLength());
    disabled(headline, () => this.isReadOnly());
  });

  isUpdatingHeadline = signal(false);
  isUpdatingLogo = signal(false);

  ecommerceCreativeMandatory = computed<boolean>(() => !!this.strategy()?.ecommerceCreativeMandatory);
  isUpdatingEcommerceCreativeMandatory = signal(false);

  saveHeadline() {
    if (this.isReadOnly()) return;
    if (!this.headlineForm().dirty()) return;

    this.isUpdatingHeadline.set(true);
    this.updateStrategy({ headline: this.headlineForm().value() || undefined }).subscribe({
      next: () => {
        this.toasterService.success(this.translocoService.translate("dsp-strategy-page.headline_successfully_updated"));
        this.isUpdatingHeadline.set(false);
        this.headlineForm().reset();
      },
      error: () => {
        this.toasterService.error(this.translocoService.translate("dsp-strategy-page.headline_update_error"));
        this.isUpdatingHeadline.set(false);
      },
    });
  }

  saveLogoAsset(logoAssetId: string) {
    this.isUpdatingLogo.set(true);
    this.updateStrategy({ logoAssetId: logoAssetId }).subscribe({
      next: () => {
        this.isUpdatingLogo.set(false);
        this.toasterService.success(this.translocoService.translate("dsp-strategy-page.logo_successfully_updated"));
      },
      error: () => {
        this.isUpdatingLogo.set(false);
        this.toasterService.error(this.translocoService.translate("dsp-strategy-page.logo_update_error"));
      },
    });
  }
  uploadLogo(file: File) {
    const ref = this.modalService.openModal(UploadAmazonLoadingModalComponent, {
      closeMode: "none",
    });
    this.dspCreativeAssetsService
      .uploadCreativeAsset(
        this.accountMarketplace().accountId,
        this.accountMarketplace().marketplace,
        this.accountMarketplace().dspAdvertiserId!,
        file,
        MediaType.brandLogo,
        this.accountMarketplace().dspAdvertiser!.entityId,
      )
      .subscribe({
        next: (creativeAsset) => {
          this.saveLogoAsset(creativeAsset.assetId);
          this.toasterService.success("Logo uploaded successfully");
          ref.close();
        },
        error: (err) => {
          ref.close();
          this.toasterService.error(err, "Logo upload failed");
        },
      });
  }

  deleteLogoAsset() {
    this.modalService
      .openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
        modalTitle: this.translocoService.translate("dsp-strategy-page.delete_logo_title"),
        data: {
          message: this.translocoService.translate("dsp-strategy-page.delete_logo_warning"),
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((confirmed) => confirmed),
        tap(() => this.isUpdatingLogo.set(true)),
        switchMap(() => this.updateStrategy({ logoAssetId: undefined })),
      )
      .subscribe({
        next: () => {
          this.isUpdatingLogo.set(false);
          this.toasterService.success(this.translocoService.translate("dsp-strategy-page.logo_successfully_removed"));
        },
        error: () => {
          this.isUpdatingLogo.set(false);
          this.toasterService.error(this.translocoService.translate("dsp-strategy-page.logo_remove_error"));
        },
      });
  }

  toggleEcommerceCreativeMandatory(isMandatory: boolean | undefined) {
    this.isUpdatingEcommerceCreativeMandatory.set(true);
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        take(1),
        switchMap((am) =>
          this.strategyService.updateStrategy({
            accountId: am.accountId,
            marketplace: am.marketplace,
            organizationId: am.resourceOrganizationId!,
            strategyId: this.strategy()!.strategyId,
            asinsToAdd: [],
            asinsToDelete: [],
            ecommerceCreativeMandatory: !!isMandatory,
          }),
        ),
      )
      .subscribe({
        next: () => {
          this.isUpdatingEcommerceCreativeMandatory.set(false);
        },
        error: () => {
          this.isUpdatingEcommerceCreativeMandatory.set(false);
        },
      });
  }

  private updateStrategy(params: Partial<StrategyUpdateParams<CampaignType.DSP>>) {
    return this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      take(1),
      switchMap((am) =>
        this.strategyService.updateStrategy({
          accountId: am.accountId,
          marketplace: am.marketplace,
          organizationId: am.resourceOrganizationId!,
          strategyId: this.strategy()!.strategyId,
          asinsToAdd: [],
          asinsToDelete: [],
          ...params,
        }),
      ),
    );
  }
}
